import type { DisplayLocationRead } from '../../data/contracts/displayLocationRead';

/**
 * The columns the filter rules read; lets the API apply them to a slim projection.
 * `tags` and `name` are optional so rows read for holiday counts alone still fit.
 */
export type FilterableDisplay = Pick<DisplayLocationRead, 'holidays' | 'holiday_type' | 'display_type' | 'fm_station'> & {
  tags?: string[] | null;
  name?: string | null;
};

/**
 * Filter rules for the /displays/in/... landing pages. The city landing page
 * and the display detail SEO footer both use these, so a footer link is only
 * shown when the page it opens will list at least one display.
 */

// Canonical holiday labels (mirrors DisplaysMap's HOLIDAY_FILTERS) used to
// normalize ?holiday= params like "christmas" -> "Christmas".
export const KNOWN_HOLIDAYS = [
  'Christmas',
  'Halloween',
  'Easter',
  'Fourth of July',
  'Thanksgiving',
  "Valentine's Day",
];

// ?type= slug -> human label for headings/copy. Musical/synchronized displays
// have no DB boolean; the repo-wide signal is a non-empty fm_station (see
// DisplaysOverview's synchronized count), so that drives the filter.
export const TYPE_LABELS: Record<string, string> = {
  musical: 'Synchronized',
  synchronized: 'Synchronized',
  'drive-thru': 'Drive-through',
  'drive-through': 'Drive-through',
  'walk-through': 'Walk-through',
};

export const resolveHolidayLabel = (holidayParam: string): string => {
  const trimmed = holidayParam.trim();
  if (!trimmed) return '';
  return KNOWN_HOLIDAYS.find((holiday) => holiday.toLowerCase() === trimmed.toLowerCase()) ?? trimmed;
};

export const resolveTypeLabel = (typeParam: string): string => TYPE_LABELS[typeParam.trim().toLowerCase()] ?? '';

export const displayHolidays = (display: FilterableDisplay): string[] =>
  display.holidays && display.holidays.length > 0
    ? display.holidays
    : display.holiday_type
      ? [display.holiday_type]
      : [];

export const displayTypeMatches = (display: FilterableDisplay, needle: string): boolean => {
  if (!display.display_type) return false;
  const values = Array.isArray(display.display_type)
    ? display.display_type.map(String)
    : [display.display_type];
  return values.some((value) => value.toLowerCase().includes(needle.toLowerCase()));
};

export const isSynchronized = (display: FilterableDisplay): boolean =>
  Boolean(display.fm_station && display.fm_station.trim() !== '');

// "Drive-Through", "drive thru", "Drive-Through Haunt" -> "drivethrough...".
// A prefix match, so FrightMaps' "Drive-Through Haunt" and "Walk-Through Haunted House" count.
const tagKey = (value: string): string => value.toLowerCase().replace(/[^a-z]/g, '');
const DRIVE_TAGS = ['drivethrough', 'drivethru'];
const WALK_TAGS = ['walkthrough', 'walkthru'];
const DRIVE_NAME = /\bdrive[\s-]*(thru|through)\b/i;

const hasTag = (display: FilterableDisplay, prefixes: string[]): boolean =>
  (display.tags ?? []).some((tag) => typeof tag === 'string' && prefixes.some((p) => tagKey(tag).startsWith(p)));

/**
 * Drive-through shows. display_type holds the venue (Residential, Commercial),
 * so the signal is the canonical Drive-Through tag, then a name like
 * "Drive-Thru Christmas on Merry Lane". display_type is kept as a fallback.
 */
export const isDriveThrough = (display: FilterableDisplay): boolean =>
  hasTag(display, DRIVE_TAGS) ||
  (typeof display.name === 'string' && DRIVE_NAME.test(display.name)) ||
  displayTypeMatches(display, 'drive-through');

/** Walk-through trails: the Walk-Through tag, or display_type as a fallback. */
export const isWalkThrough = (display: FilterableDisplay): boolean =>
  hasTag(display, WALK_TAGS) || displayTypeMatches(display, 'walk-through');

/** Apply the landing page's ?holiday= / ?type= filters (labels from the resolve helpers). */
export const filterDisplaysByLabels = <T extends FilterableDisplay>(
  rows: T[],
  holidayLabel: string,
  typeLabel: string,
): T[] =>
  rows.filter((display) => {
    if (holidayLabel) {
      const matchesHoliday = displayHolidays(display).some(
        (holiday) => holiday.trim().toLowerCase() === holidayLabel.toLowerCase(),
      );
      if (!matchesHoliday) return false;
    }
    if (typeLabel === 'Synchronized' && !isSynchronized(display)) return false;
    if (typeLabel === 'Drive-through' && !isDriveThrough(display)) return false;
    if (typeLabel === 'Walk-through' && !isWalkThrough(display)) return false;
    return true;
  });

/** Convenience: filter by raw query-param values, exactly as the landing page reads them. */
export const filterDisplaysByParams = <T extends FilterableDisplay>(
  rows: T[],
  params: { holiday?: string; type?: string },
): T[] =>
  filterDisplaysByLabels(rows, resolveHolidayLabel(params.holiday ?? ''), resolveTypeLabel(params.type ?? ''));
